import type {
    ParticleField,
    ParticleGeometry,
    ParticleSettings,
} from "./ParticleTypes";
import { PARTICLE_STEPS_PER_FT } from "./ParticleTypes";

// 境界流入の再発生分布 (旧 2D 実装 GPV-1.6 mydraw.js の _ex_xy/_ex_datFS の移植)。
// 風が吹き抜ける場で風上端が疎にならないよう、有効領域の外周線分上に
// 「流入方向の風速に比例した確率」で粒子を再発生させるための共有ロジック。
// WebGL 版はテクスチャ化して shader で逆引き、2D 版は配列のまま二分探索する。

export interface InflowSegments {
    n: number;
    xy: Float32Array; // 線分毎に (x, y, dx, dy)。位置 = (x,y) + t*(dx,dy)
    direction: Uint8Array; // 0=下から 1=上から 2=右から 3=左から
    gridIndex: Uint32Array; // 流入風速を参照する格子頂点 (旧実装と同じ代表1点)
    validCount: number; // 有効頂点数 (一様発生との混合比の分母)
}

// 有効領域の外周線分を構築する (格子ウィンドウ毎に1回)。
// 旧実装と同じ近似: 各行/列の最初と最後の有効セルのみ (凹领域の内側境界は対象外)。
export function buildInflowSegments(
    field: ParticleField,
    geometry: ParticleGeometry,
): InflowSegments {
    const w = field.width;
    const h = field.height;
    const valid = (index: number) => field.u[index] !== 32767;
    const cell = new Uint8Array((w - 1) * (h - 1));
    let validCount = 0;
    for (let index = 0; index < w * h; index++) {
        if (valid(index)) {
            validCount++;
        }
    }
    for (let y = 0; y < h - 1; y++) {
        for (let x = 0; x < w - 1; x++) {
            cell[y * (w - 1) + x] =
                valid(y * w + x) &&
                valid(y * w + x + 1) &&
                valid((y + 1) * w + x) &&
                valid((y + 1) * w + x + 1)
                    ? 1
                    : 0;
        }
    }
    const xs = geometry.gridDx * geometry.scale;
    const ys = geometry.gridDy * geometry.scale;
    const xy: number[] = [];
    const direction: number[] = [];
    const gridIndex: number[] = [];
    for (let y = 0; y < h - 1; y++) {
        for (let x = 0; x < w - 1; x++) {
            if (cell[y * (w - 1) + x]) {
                // 左から
                xy.push(x * xs + geometry.rx, y * ys + geometry.ry, 0, ys);
                direction.push(3);
                gridIndex.push(y * w + x);
                break;
            }
        }
        for (let x = w - 2; x > 0; x--) {
            if (cell[y * (w - 1) + x]) {
                // 右から
                xy.push(
                    (x + 1 - 0.0001) * xs + geometry.rx,
                    y * ys + geometry.ry,
                    0,
                    ys,
                );
                direction.push(2);
                gridIndex.push(y * w + x + 1);
                break;
            }
        }
    }
    for (let x = 0; x < w - 1; x++) {
        for (let y = 0; y < h - 1; y++) {
            if (cell[y * (w - 1) + x]) {
                // 上から
                xy.push(x * xs + geometry.rx, y * ys + geometry.ry, xs, 0);
                direction.push(1);
                gridIndex.push(y * w + x);
                break;
            }
        }
        for (let y = h - 2; y > 0; y--) {
            if (cell[y * (w - 1) + x]) {
                // 下から
                xy.push(
                    x * xs + geometry.rx,
                    (y + 1 - 0.0001) * ys + geometry.ry,
                    xs,
                    0,
                );
                direction.push(0);
                gridIndex.push((y + 1) * w + x);
                break;
            }
        }
    }
    return {
        n: direction.length,
        xy: new Float32Array(xy),
        direction: new Uint8Array(direction),
        gridIndex: new Uint32Array(gridIndex),
        validCount,
    };
}

// FTa の風から流入 CDF (正規化済み) と境界発生の混合比を計算する (FT 時間毎)。
// cdf は長さ segments.n+1 以上のバッファを呼び出し側が用意する (cdf[0]=0)。
// 戻り値 = 混合比 ex_rate (0 なら境界発生なし)。
// 旧実装と同式: ex:一様 = 流入速度合計×寿命×セル辺 : 有効面積 (px 単位で等価)。
export function computeInflowCdf(
    segments: InflowSegments,
    field: ParticleField,
    ftA: number,
    geometry: ParticleGeometry,
    settings: ParticleSettings,
    cdf: Float32Array,
): number {
    if (!segments.n) {
        return 0;
    }
    const n = field.width * field.height;
    const offset = Math.min(Math.max(ftA, 0), field.timeCount - 1) * n;
    cdf[0] = 0;
    for (let index = 0; index < segments.n; index++) {
        const gi = offset + segments.gridIndex[index];
        const rawU = field.u[gi];
        const u = rawU === 32767 ? 0 : rawU / 100000;
        const v = rawU === 32767 ? 0 : field.v[gi] / 100000;
        const d = segments.direction[index];
        let inbound: number;
        if (d === 0) {
            inbound = v > 0 ? v : 0; // 下から (画面上向き = +v で流入)
        } else if (d === 1) {
            inbound = v < 0 ? -v : 0; // 上から
        } else if (d === 2) {
            inbound = u < 0 ? -u : 0; // 右から
        } else {
            inbound = u > 0 ? u : 0; // 左から
        }
        cdf[index + 1] = cdf[index] + inbound;
    }
    const total = cdf[segments.n];
    if (total <= 0) {
        return 0;
    }
    const speedPx =
        (3600 / PARTICLE_STEPS_PER_FT) * geometry.scale * settings.speed;
    const maxAge = settings.age * PARTICLE_STEPS_PER_FT;
    const boundary =
        total * speedPx * maxAge * geometry.gridDx * geometry.scale;
    const area =
        segments.validCount *
        geometry.gridDx *
        geometry.gridDy *
        geometry.scale *
        geometry.scale;
    const inverse = 1 / total;
    for (let index = 1; index <= segments.n; index++) {
        cdf[index] *= inverse;
    }
    return boundary / (boundary + area);
}
